import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
} from "@mui/icons-material";
import { usePlan } from "../../hooks/usePlan";

interface Props {
  projectId: string;
}

export function ClientPlanReview({ projectId }: Props) {
  const { plan, loading, saving, error, fetchPlan, approvePlan, requestChanges } =
    usePlan(projectId);

  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [changesDialogOpen, setChangesDialogOpen] = useState(false);
  const [changesNotes, setChangesNotes] = useState("");

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  const totalPercent =
    plan?.milestones.reduce((sum, m) => sum + Number(m.percentage_of_project), 0) ?? 0;

  const totalEstimatedCost =
    plan?.milestones.reduce((sum, m) => sum + Number(m.estimated_cost ?? 0), 0) ?? 0;

  const handleApprove = async () => {
    setApproveConfirmOpen(false);
    await approvePlan(plan?.milestones ?? []);
  };

  const handleRequestChanges = async () => {
    setChangesDialogOpen(false);
    await requestChanges(changesNotes);
    setChangesNotes("");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!plan || plan.plan_status === "draft") {
    return (
      <Box
        sx={{
          py: 8,
          textAlign: "center",
          border: "2px dashed",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <PendingIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          Your contractor hasn't submitted a plan yet.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Once they send it for review, it will appear here.
        </Typography>
      </Box>
    );
  }

  if (plan.plan_status === "approved") {
    return (
      <Stack spacing={3}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CheckCircleIcon color="success" />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Plan Approved
            </Typography>
            {plan.plan_approved_at && (
              <Typography variant="caption" color="text.secondary">
                Approved on {new Date(plan.plan_approved_at).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        </Box>

        <Alert severity="success">
          You approved this plan. Project milestones have been set and work can begin.
        </Alert>

        <PlanDetails plan={plan} totalPercent={totalPercent} totalEstimatedCost={totalEstimatedCost} />
      </Stack>
    );
  }

  // pending_approval
  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Project Plan — Ready for Review
          </Typography>
          {plan.plan_submitted_at && (
            <Typography variant="caption" color="text.secondary">
              Submitted {new Date(plan.plan_submitted_at).toLocaleDateString()}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="warning"
            disabled={saving}
            onClick={() => setChangesDialogOpen(true)}
          >
            Request changes
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            onClick={() => setApproveConfirmOpen(true)}
          >
            Approve plan
          </Button>
        </Stack>
      </Box>

      <Alert severity="info">
        Review the plan below. Approving will activate the project milestones and allow work to begin.
        If anything needs adjusting, click "Request changes" to send it back to your contractor.
      </Alert>

      <PlanDetails plan={plan} totalPercent={totalPercent} totalEstimatedCost={totalEstimatedCost} />

      {/* Approve confirm */}
      <Dialog open={approveConfirmOpen} onClose={() => setApproveConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve this plan?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            By approving, you confirm you agree to the scope, milestones, and estimated costs
            outlined. The project will move to active status and work can begin.
          </Typography>
          <Typography variant="caption" color="text.disabled">
            This approval is for project planning purposes only and does not
            constitute a legally binding contract between you and your
            contractor.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setApproveConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={saving}>
            Approve plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request changes dialog */}
      <Dialog open={changesDialogOpen} onClose={() => setChangesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request changes</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This will send the plan back to your contractor for revisions.
            </Typography>
            <TextField
              label="Notes for your contractor"
              multiline
              minRows={3}
              fullWidth
              placeholder="Describe what needs to change — e.g. adjusted timeline, different materials, scope clarification..."
              value={changesNotes}
              onChange={(e) => setChangesNotes(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setChangesDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleRequestChanges} disabled={saving}>
            Send feedback
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function PlanDetails({
  plan,
  totalPercent,
  totalEstimatedCost,
}: {
  plan: NonNullable<ReturnType<typeof usePlan>["plan"]>;
  totalPercent: number;
  totalEstimatedCost: number;
}) {
  return (
    <Stack spacing={2}>
      {/* Scope */}
      {(plan.plan_scope || plan.plan_notes) && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Scope of Work
            </Typography>
            {plan.plan_scope && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Project scope
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                  {plan.plan_scope}
                </Typography>
              </Box>
            )}
            {plan.plan_notes && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Assumptions & exclusions
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                  {plan.plan_notes}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Milestones */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Milestones
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip label={`${totalPercent}% allocated`} size="small" color={totalPercent === 100 ? "success" : "warning"} variant="outlined" />
              {totalEstimatedCost > 0 && (
                <Chip label={`$${totalEstimatedCost.toLocaleString()} total est.`} size="small" variant="outlined" />
              )}
            </Stack>
          </Box>

          {plan.milestones.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No milestones in this plan.
            </Typography>
          ) : (
            <Stack spacing={0}>
              {plan.milestones.map((m, idx) => (
                <Box key={m.id}>
                  {idx > 0 && <Divider />}
                  <Box sx={{ py: 2, display: "flex", gap: 2, alignItems: "flex-start" }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        bgcolor: "primary.light",
                        color: "primary.contrastText",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {idx + 1}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {m.title}
                      </Typography>
                      {m.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                          {m.description}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Project share
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {m.percentage_of_project}%
                          </Typography>
                        </Box>
                        {m.estimated_cost && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Estimated cost
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              ${Number(m.estimated_cost).toLocaleString()}
                            </Typography>
                          </Box>
                        )}
                        {m.target_date && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Target date
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {new Date(m.target_date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
